import { InputNumber, Switch, Typography } from "antd";
import styled from "styled-components/macro";

const { Text } = Typography;

const DEFAULT_CHART_MAX_BINS = 10;

interface ChartBinsSettingsProps {
  active: boolean;
  maxBins: Maybe<number>;
  onChange: (active: boolean, maxBins: Nullable<number>) => void;
  testIdPrefix: string;
}

/**
 * A chart's own Max Bins over the focus bins. Off follows the focus; on keeps the
 * top Max Bins groups and folds the rest into Others, or shows every group when empty.
 */
function ChartBinsSettings({
  active,
  maxBins,
  onChange,
  testIdPrefix
}: ChartBinsSettingsProps) {
  return (
    <Content data-testid={`${testIdPrefix}-bin-settings`}>
      <Header>
        <Text strong>Chart Bins</Text>
        <Switch
          data-testid={`${testIdPrefix}-bin-settings-switch`}
          aria-label="Chart Bins"
          size="small"
          checked={active}
          onChange={(checked: boolean) =>
            onChange(checked, maxBins === undefined ? DEFAULT_CHART_MAX_BINS : maxBins)
          }
        />
      </Header>
      {active ? (
        <Row>
          <Text>Max Bins</Text>
          <InputNumber
            data-testid={`${testIdPrefix}-max-bins-input`}
            aria-label="Max Bins"
            size="small"
            min={1}
            precision={0}
            placeholder="All"
            value={maxBins}
            onChange={(value) => {
              // Empty shows every group, as an empty focus Max Bins does. A decimal
              // waits for the blur, which rounds it to a whole number.
              if (value == null) onChange(true, null);
              else if (Number.isInteger(value) && value >= 1) onChange(true, value);
            }}
          />
        </Row>
      ) : (
        <Text type="secondary">Following the focus bins</Text>
      )}
    </Content>
  );
}

export default ChartBinsSettings;

const Content = styled.div`
  display: grid;
  gap: 8px;
  min-width: 200px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;
